import { Link } from "@inertiajs/react";
import { ChevronLeft, Users } from "lucide-react";
import { useMemo } from "react";
import BizminerLayout from "@/bizminer/layouts/app/BizminerLayout.tsx";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { getIcon } from "@/lib/icons";
import { scoring_bizminer_app_pillar_insight_path } from "@/routes";
import type {
  BizminerPeerData,
  BizminerPillarInsight,
} from "@/types/serializers";
import { PeerSelection } from "../_components/PeerSelection";
import { useScoringPeers } from "../_components/useScoringPeers";
import { CategoryScoresSidebar } from "./_components/CategoryScoresSidebar";
import { PeerComparisonBars } from "./_components/PeerComparisonBars";
import { SubcategoryDriverList } from "./_components/SubcategoryDriverList";
import type { CategoryDetail } from "./_components/scoring-category-types";

interface Props {
  pillar_insight: BizminerPillarInsight;
  peer_data: BizminerPeerData;
  category_detail: CategoryDetail;
}

export default function ScoringCategory({
  pillar_insight,
  peer_data,
  category_detail,
}: Props) {
  const IconComponent = getIcon(pillar_insight.icon) || Users;
  const { category } = category_detail;

  const {
    peer_pool,
    selected_peer_ids,
    peer_columns,
    categories_with_peers,
    addPeer,
    removePeer,
  } = useScoringPeers({
    peerData: peer_data,
    pillarInsightId: pillar_insight.id,
  });

  const activePeerScores = useMemo(
    () =>
      categories_with_peers.find((c) => c.id === category.id)
        ?.competitor_scores ?? [],
    [categories_with_peers, category.id],
  );

  return (
    <BizminerLayout>
      <div className="space-y-6">
        {/* Breadcrumb + heading */}
        <div className="mb-6">
          <Link
            href={scoring_bizminer_app_pillar_insight_path(pillar_insight.id)}
            className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-primary transition-colors mb-2"
          >
            <ChevronLeft className="size-3.5" />
            <span>Scoring & Assessment</span>
            <span className="text-muted-foreground/50">&middot;</span>
            <span>{pillar_insight.title}</span>
          </Link>
          <div className="flex items-center gap-3">
            <div className="flex size-10 shrink-0 items-center justify-center rounded-lg border border-border/50 bg-muted/30">
              <IconComponent className="size-5 text-primary" />
            </div>
            <h1 className="text-2xl font-bold text-foreground truncate">
              {category.name}
            </h1>
          </div>
        </div>

        <PeerSelection
          selectedPeerIds={selected_peer_ids}
          peerPool={peer_pool}
          peerColumns={peer_columns}
          onAdd={addPeer}
          onRemove={removePeer}
        />

        {/* Peer Comparison + Category Scores */}
        <div className="grid gap-4 grid-cols-[3fr_1fr]">
          <Card>
            <CardHeader className="px-4 py-1.5">
              <CardTitle className="text-sm text-muted-foreground">
                Peer Comparison
              </CardTitle>
            </CardHeader>
            <CardContent className="px-4 pt-0 pb-1.5">
              <PeerComparisonBars
                accountName={pillar_insight.account_name}
                accountScore={category.account_score}
                peerColumns={peer_columns}
                peerScores={activePeerScores}
              />
            </CardContent>
          </Card>

          <CategoryScoresSidebar
            categoriesWithPeers={categories_with_peers}
            activeCategoryId={category.id}
            pillarInsightId={pillar_insight.id}
          />
        </div>

        {/* Subcategory KPI Drivers */}
        <SubcategoryDriverList
          detailData={category_detail}
          accountName={pillar_insight.account_name}
          peerColumns={peer_columns}
          isLoading={false}
          error={null}
        />
      </div>
    </BizminerLayout>
  );
}
